import { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '@/stores/auth';
import { useLock } from '@/stores/lock';
import { openSettingsPage, type SectionId } from '@/screens/settings/pages';
import { signOutSafely } from './actions';

interface Entry {
  label: string;
  hint?: string;
  run: () => void;
}

/**
 * Whose window this is, and what they can do about it: account, password,
 * lock, sign out.
 *
 * These used to sit along the bottom of the sidebar, where they competed with
 * navigation for the eye and disappeared entirely on a narrow screen. A
 * desktop application keeps them where every other window-level control is —
 * the far end of the top bar, behind the person's own initials.
 */
export function AccountMenu({ onNavigate }: { onNavigate: (screenId: string) => void }) {
  const user = useAuth((s) => s.user);
  const lock = useLock((s) => s.lock);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (listRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    listRef.current?.querySelector<HTMLButtonElement>('button')?.focus();

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const initials = user.fullName
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('');

  const settings = (id: SectionId) => () => openSettingsPage(id, onNavigate);

  const entries: Entry[] = [
    { label: 'My account', run: settings('account') },
    { label: 'Password & sign-in', run: settings('password') },
    { label: 'Lock the screen', hint: 'Ctrl+Shift+L', run: lock },
    { label: 'Sign out', run: () => void signOutSafely() },
  ];

  function onListKeyDown(e: React.KeyboardEvent<HTMLUListElement>) {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    if (e.key === 'ArrowDown') next = (index + 1) % items.length;
    else if (e.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else return;
    e.preventDefault();
    items[next]?.focus();
  }

  return (
    <div className="accountmenu">
      <button
        ref={buttonRef}
        type="button"
        className="accountmenu__button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Your account, ${user.fullName}`}
        title={user.fullName}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">{initials}</span>
      </button>

      {open && (
        <ul id={menuId} ref={listRef} role="menu" className="accountmenu__list" onKeyDown={onListKeyDown}>
          <li role="none" className="accountmenu__who">
            <span className="accountmenu__name">{user.fullName}</span>
            <span className="accountmenu__role">{(user.roleNames ?? user.roles).join(', ')}</span>
          </li>
          {entries.map((entry) => (
            <li key={entry.label} role="none">
              <button
                type="button"
                role="menuitem"
                className="accountmenu__item"
                onClick={() => {
                  setOpen(false);
                  entry.run();
                }}
              >
                <span>{entry.label}</span>
                {entry.hint && <span className="accountmenu__hint">{entry.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
